// Global WebSocket manager to handle subscriptions across React renders
class WebSocketManager {
  constructor() {
    this.ws = null;
    this.pendingSubscriptions = new Map();
    this.isProcessing = false;
  }

  setWebSocket(ws) {
    if (!ws) {
      console.log('📡 WebSocket manager: Cannot set null WebSocket');
      return;
    }
    
    this.ws = ws;
    console.log('📡 WebSocket manager: WebSocket set, readyState:', ws.readyState);
    
    // Wait for WebSocket to be fully open before processing
    if (ws.readyState === 1) { // OPEN
      console.log('📡 WebSocket manager: WebSocket already open, processing immediately');
      this.processPendingSubscriptions();
    } else if (ws.readyState === 0) { // CONNECTING
      console.log('📡 WebSocket manager: WebSocket still connecting, waiting for open event');
      // Don't process yet - let the onopen handler in useWebSocket do it
    } else {
      console.log('📡 WebSocket manager: WebSocket in unexpected state:', ws.readyState);
    }
  }

  addSubscription(id, subscription) {
    if (!subscription.sent && this.ws && this.ws.readyState === 1) {
      // Try to send immediately
      try {
        this.ws.send(JSON.stringify({
          type: subscription.type,
          payload: subscription.payload
        }));
        subscription.sent = true;
        console.log(`📡 Sent subscription immediately: ${subscription.type} (${id})`);
        return true;
      } catch (error) {
        console.error(`Failed to send subscription ${id}:`, error);
      }
    }
    
    // Queue it if not sent
    if (!subscription.sent) {
      this.pendingSubscriptions.set(id, subscription);
      console.log(`📡 Queued subscription: ${subscription.type} (${id})`);
      // Try to process pending subscriptions
      this.processPendingSubscriptions();
    }
    return false;
  }

  processPendingSubscriptions() {
    if (this.isProcessing || !this.ws || this.ws.readyState !== 1) {
      console.log(`📡 Cannot process pending subscriptions - isProcessing: ${this.isProcessing}, ws exists: ${!!this.ws}, readyState: ${this.ws?.readyState}`);
      return;
    }

    this.isProcessing = true;
    const toRemove = [];

    console.log(`📡 Processing ${this.pendingSubscriptions.size} pending subscriptions`);

    for (const [id, subscription] of this.pendingSubscriptions.entries()) {
      if (!subscription.sent && this.ws.readyState === 1) {
        try {
          this.ws.send(JSON.stringify({
            type: subscription.type,
            payload: subscription.payload
          }));
          subscription.sent = true;
          toRemove.push(id);
          console.log(`📡 Sent pending subscription: ${subscription.type} (${id})`);
        } catch (error) {
          console.error(`Failed to send pending subscription ${id}:`, error);
        }
      }
    }

    // Remove sent subscriptions
    toRemove.forEach(id => this.pendingSubscriptions.delete(id));
    
    if (toRemove.length > 0) {
      console.log(`📡 Processed ${toRemove.length} pending subscriptions, ${this.pendingSubscriptions.size} remaining`);
    }

    this.isProcessing = false;
  }

  clear() {
    this.ws = null;
    this.pendingSubscriptions.clear();
    this.isProcessing = false;
  }
}

// Create a singleton instance
export const wsManager = new WebSocketManager();